// @flow
import fp from "lodash/fp"
import { createSelector, type InputSelector } from "reselect"
import { groupBy, compose, map, reduce, inc } from "ramda"
import { PASSENGER_CATEGORIES } from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import type Flight from "common/records/Flight"
import type { ExtrasId, FlightId } from "mmb/types/primitiveTypes"
import type { Bags, Baggage } from "mmb/common/types/baggageTypes"
import { BAGS_TYPES } from "../../../services/bags/tools"
import {
  currentBookingFirstFlightSelector,
  bookingDataSelector,
  bookingPassengersSelector,
} from "../../../services/selector"
import { SERVICES_STATUSES } from "mmb/consts/additionalServices"
import type { BaggageCounts } from "../../AdditionalServices/scenes/AdditionalServicesStatus/types"

const BAGS_ORDER = [
  BAGS_TYPES.PERSONAL_ITEM,
  BAGS_TYPES.CABIN_BAG,
  BAGS_TYPES.HOLD_BAG,
  BAGS_TYPES.STROLLER,
]

export const baggagesDataSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  data => data.baggages,
)

export const additionalServicesListSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  data => data.additionalServices.reduce((acc, curr) => ({ ...acc, [curr.get("id")]: curr }), {}),
)

export const baggagesSelector: $FlowFixMe = createSelector(
  [baggagesDataSelector, (state, props) => props && props.paxId],
  (allBaggages, paxId) => {
    let bags = allBaggages
    if (paxId) {
      bags = allBaggages.filter(bag => bag.pid === paxId)
    }

    const groupedBagsPerFlight = fp.groupBy(bag => bag.flightId, bags)

    const sortedBaggage =
      fp.values(groupedBagsPerFlight)[0] &&
      fp.values(groupedBagsPerFlight)[0].sort((a, b) => {
        if (BAGS_ORDER.indexOf(a.category) < BAGS_ORDER.indexOf(b.category)) return -1
        if (BAGS_ORDER.indexOf(a.category) > BAGS_ORDER.indexOf(b.category)) return 1
        return 0
      })

    return sortedBaggage || []
  },
)

const reducedBags = (acc, { category, bag }) => ({
  ...acc,
  [category]: {
    bag,
    count: acc[category] ? inc(acc[category].count) : 1,
  },
})

const filterByServiceIdAndFlight = (serviceId: ExtrasId, flightId: FlightId) => ({
  additionalBookingId,
  flightFid,
}: Baggage) => additionalBookingId === serviceId && flightFid === flightId

export const baggageCountsForServiceSelector: number => InputSelector<*, *, BaggageCounts> = (
  serviceId: number,
) =>
  createSelector(
    [baggagesDataSelector, currentBookingFirstFlightSelector],
    (allBaggages: Array<Baggage>, flight: Flight): ?BaggageCounts => {
      const bagsForService = allBaggages.filter(filterByServiceIdAndFlight(serviceId, flight.id))
      return bagsForService.length === 0
        ? undefined
        : compose(
            map(reduce(reducedBags, {})),
            groupBy(b => (b.pid ? String(b.pid) : "UNKNOWN_PAX")), // CS extra may not have pid
          )(bagsForService)
    },
  )

/**
 * Is the first segment in the itinerary departing in more than X hours
 */
export const isChangeAllowedInBaggageSelector: InputSelector<any, *, boolean> = createSelector(
  [state => state.booking.data.config.allowedToChange.bags, currentBookingFirstFlightSelector],
  (allowedToChangeConfig, firstFlight: Flight) => {
    return firstFlight.getHoursToDeparture() >= allowedToChangeConfig
  },
)

// groupBy doesn't support more arguments, it must be one string
export const groupedBaggagesSelector: $FlowFixMe = createSelector(
  [baggagesSelector, additionalServicesListSelector],
  (bags, additionalServices) =>
    fp.groupBy(
      bag =>
        `${bag.bag.category}--${bag.bag.height}--${bag.bag.length}--${bag.bag.weight}--${
          bag.bag.width
        }--${String(
          !!bag.additionalBookingId &&
            additionalServices[bag.additionalBookingId] &&
            additionalServices[bag.additionalBookingId].get("status") !==
              SERVICES_STATUSES.CONFIRMED,
        )}`,
      bags,
    ),
)

// todo: in addBags we should maybe remove ordered bags
export const baggagesCountSelector: (state: any, props: any) => Array<Bags> = createSelector(
  [groupedBaggagesSelector, bookingPassengersSelector, additionalServicesListSelector],
  (baggages, passengers, additionalServices) =>
    Object.keys(baggages).reduce(
      (acc, groupedBags) => [
        ...acc,
        {
          count: baggages[groupedBags].length,
          data: baggages[groupedBags][0],
          passengers: baggages[groupedBags].map(p => fp.find(pax => pax.id === p.pid, passengers)),
          orderStatus:
            baggages[groupedBags][0].additionalBookingId &&
            additionalServices[baggages[groupedBags][0].additionalBookingId] &&
            additionalServices[baggages[groupedBags][0].additionalBookingId].get("status"),
        },
      ],
      [],
    ),
)

export const strollerSelector: $FlowFixMe = createSelector(
  [bookingDataSelector, bookingPassengersSelector],
  (data, passengers) => {
    const infantsCount = passengers.filter(p => p.category === PASSENGER_CATEGORIES.infant).length

    return (
      data.infantsConditions.trolley &&
      Boolean(infantsCount) && {
        data: { bag: { category: BAGS_TYPES.STROLLER } },
        count: infantsCount,
      }
    )
  },
)

export const strollerPerPaxSelector: $FlowFixMe = createSelector(
  [bookingDataSelector, bookingPassengersSelector, (state, props) => props && props.paxId],
  (data, passengers, paxId) => {
    const infantsCount = passengers.find(
      p => p.category === PASSENGER_CATEGORIES.infant && p.id === paxId,
    )

    return (
      data.infantsConditions.trolley &&
      infantsCount && {
        data: { bag: { category: BAGS_TYPES.STROLLER } },
        count: 1,
      }
    )
  },
)
